import { useEffect, useMemo, useRef, useState } from 'react'
import { api } from './api'
import type { Revision } from './types'

/**
 * How long a departed lock stays listed in the lock bar after it is actually released.
 *
 * A one-shot `write_part` opens and closes its revision inside a single HTTP round trip — 12 ms
 * measured, under one frame at 60 fps — so without this the lock bar would never paint at all and
 * the member would swear the agent had done nothing. This is presentation only: it pads the LIST,
 * never the editor decorations, which stay on the live set. Shading a § as locked after the lock
 * is gone would be a lie the member could act on.
 */
export const LOCK_AFTERGLOW_MS = 2500

/** When the lock lapses, or null if it never will (a legacy record with no `expiresAt`). */
export function lockLapsesAt(rev: Pick<Revision, 'expiresAt'>): number | null {
  if (!rev.expiresAt) return null
  const at = Date.parse(rev.expiresAt)
  return Number.isFinite(at) ? at : null
}

/** Parity with backend/domain/locks.ts#isExpired — a record with no expiry counts as expired, so
 *  a crashed console agent from before the TTL cannot wedge a part forever. Shared by Canvas (the
 *  open room's lock bar) and lib/wide-replace.ts (a background room's lock guard), so the two can
 *  never disagree about whether a claim is still live. */
export function lockExpired(rev: Pick<Revision, 'expiresAt'>, now: number): boolean {
  const at = lockLapsesAt(rev)
  return at === null || at <= now
}

/**
 * The member's own userId, from `/v1/me`.
 *
 * Not used to carve locks out — a lock held under this id by the member's console agent must still
 * block their browser. It is used to LABEL such a lock, so a member blocked by their own agent can
 * tell that from a bug.
 */
export function useMyUserId(): string | null {
  const [userId, setUserId] = useState<string | null>(null)
  useEffect(() => {
    let cancelled = false
    api
      .me()
      .then((me) => {
        if (!cancelled) setUserId(me.userId)
      })
      // Identity is a nicety here; the lock view works without it.
      .catch(() => undefined)
    return () => {
      cancelled = true
    }
  }, [])
  return userId
}

/**
 * The STABLE identity of a lock: who holds which § — never which revision record currently
 * expresses it.
 *
 * WHY: while the model thinks, the console agent heartbeats its claim
 * (`client/niobe-mcp/src/registry.ts#refreshClaim` re-POSTs `/v1/revisions` every
 * max(5s, TTL/3) ≈ 100s), and the backend's `openRevision`
 * (`backend/adapters/room/hocuspocus.ts`) treats a same-actor re-claim of the same § as a refresh:
 * it SUPERSEDES the previous record, deleting it and storing a NEW uuid. Keyed on `rev.id`, every
 * heartbeat therefore reads as "that lock was released and a different one arrived" — the lock bar
 * would list the same § TWICE for the whole afterglow window and the polite live region would
 * re-speak "§X is now locked by Y's agent", both every 100 seconds of one long generation.
 *
 * `actor` + `section` is exactly the pair the backend's supersede matches on, so the key is stable
 * across a heartbeat by construction. On a slides room the scope is the claimed `slide` (a
 * same-actor re-read of the same slide is the refresh there), so it joins the key: one agent on two
 * slides is two locks, and a slide claim is not the whole-part one.
 */
export function lockKey(rev: Pick<Revision, 'actor' | 'section' | 'slide'>): string {
  // Length-prefixed so no (actor, section) pair can collide with another, whatever
  // characters a userId happens to contain.
  const base = `${rev.actor.length}:${rev.actor}:${rev.section ?? ''}`
  return rev.slide ? `${base}:slide:${rev.slide}` : base
}

interface Departed {
  rev: Revision
  at: number
}

/**
 * `live` plus the locks that left it within the last `ms`, so a very short-lived lock is still
 * perceptible. Departed entries are held with their departure time rather than one timer per
 * batch: a timer re-armed on every `live` change (locks re-render often) would keep cancelling
 * the pending removal, and ghosts would never expire.
 *
 * Keyed on `lockKey`, not on the revision id — see above: a heartbeat re-claim is the SAME lock.
 */
export function useLockAfterglow(live: Revision[], ms: number = LOCK_AFTERGLOW_MS): Revision[] {
  const [departed, setDeparted] = useState<Departed[]>([])
  const prevRef = useRef<Revision[]>([])

  useEffect(() => {
    const liveKeys = new Set(live.map(lockKey))
    const gone = prevRef.current.filter((r) => !liveKeys.has(lockKey(r)))
    prevRef.current = live
    if (gone.length === 0) return
    const at = Date.now()
    const goneKeys = new Set(gone.map(lockKey))
    setDeparted((cur) => [
      ...cur.filter((d) => !goneKeys.has(lockKey(d.rev))),
      ...gone.map((rev) => ({ rev, at })),
    ])
  }, [live])

  // Prune what has outstayed the window, then re-arm for the next oldest.
  useEffect(() => {
    if (departed.length === 0) return
    const oldest = Math.min(...departed.map((d) => d.at))
    const timer = setTimeout(
      () => {
        const cutoff = Date.now() - ms
        setDeparted((cur) => cur.filter((d) => d.at > cutoff))
      },
      Math.max(0, oldest + ms - Date.now()),
    )
    return () => clearTimeout(timer)
  }, [departed, ms])

  return useMemo(() => {
    const liveKeys = new Set(live.map(lockKey))
    // A lock that came back (or was heartbeated under a new id) keeps its live entry only —
    // never listed twice.
    return [...live, ...departed.filter((d) => !liveKeys.has(lockKey(d.rev))).map((d) => d.rev)]
  }, [live, departed])
}

/** How long the header keeps an agent's activity sentence after its claim is released, so the
 *  member can still read what was just done before it goes into History. */
export const AGENT_ACTIVITY_LINGER_MS = 5000
/** The leave animation (the sentence sliding into the History button, or a plain fade under
 *  prefers-reduced-motion). Must match the `.dt-activity` transition in styles.css. */
export const AGENT_ACTIVITY_LEAVE_MS = 600

/**
 * An agent-held claim — the ones the editor HEADER reports instead of the in-editor lock bar.
 *
 * `actorKind: 'agent'` is the console agent. A whole-part claim (no § and no slide) is always an
 * agent edit too: the web never opens a revision, so whatever holds the whole part is the AI path
 * (a legacy record without `actorKind` included — the bar has always named it "Niobe").
 */
export function isAgentLock(rev: Pick<Revision, 'actorKind' | 'section' | 'slide'>): boolean {
  return rev.actorKind === 'agent' || (!rev.section && !rev.slide)
}

export interface AgentActivity {
  /** The claim the sentence describes — the most recent live one, or the one that just ended. */
  rev: Revision
  /** How many OTHER agent claims are live alongside it ("+N more"). */
  more: number
  /** `live` while held; `linger` for AGENT_ACTIVITY_LINGER_MS after release; then `leaving`. */
  phase: 'live' | 'linger' | 'leaving'
}

/**
 * What the editor header says about agent work on the open room, and when it lands in History.
 *
 * Live agent claims show the most recent one. When the last one is released the sentence stays
 * `linger`, then `leaving` (the slide into History), then nothing — and `landed` is bumped, so
 * the History badge can pulse once. A claim arriving during linger or leave replaces it at once.
 * `scope` is the room: switching parts drops whatever the previous room was still showing.
 */
export function useAgentActivity(
  live: Revision[],
  scope: string | null,
): { activity: AgentActivity | null; landed: number } {
  const newest = useMemo(
    () => live.reduce<Revision | null>((a, r) => (!a || r.createdAt > a.createdAt ? r : a), null),
    [live],
  )
  const more = Math.max(0, live.length - 1)
  const [tail, setTail] = useState<AgentActivity | null>(null)
  const [landed, setLanded] = useState(0)
  const lastRef = useRef<{ scope: string | null; shown: Omit<AgentActivity, 'phase'> | null }>({
    scope,
    shown: null,
  })

  useEffect(() => {
    const prev = lastRef.current
    if (prev.scope !== scope) {
      lastRef.current = { scope, shown: newest ? { rev: newest, more } : null }
      setTail(null)
      return
    }
    if (newest) {
      lastRef.current = { scope, shown: { rev: newest, more } }
      setTail(null)
      return
    }
    if (prev.shown) {
      setTail({ ...prev.shown, phase: 'linger' })
      lastRef.current = { scope, shown: null }
    }
  }, [newest, more, scope])

  useEffect(() => {
    if (!tail) return
    const timer =
      tail.phase === 'linger'
        ? setTimeout(() => setTail((t) => (t ? { ...t, phase: 'leaving' } : t)), AGENT_ACTIVITY_LINGER_MS)
        : setTimeout(() => {
            setTail(null)
            setLanded((n) => n + 1)
          }, AGENT_ACTIVITY_LEAVE_MS)
    return () => clearTimeout(timer)
  }, [tail])

  return { activity: newest ? { rev: newest, more, phase: 'live' } : tail, landed }
}
